import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import Co2SimulatorPage from '../pages/co2Simulator/Co2SimulatorPage';
import ConsentsExternalPage from '../pages/consentsExternal/ConsentsExternalPage';
import NoCompanyExternalPage from '../pages/noCompanyExternal/NoCompanyExternalPage';
import { ReduxState } from '../redux/types';
import { setCompanyId } from '../redux/user/userSlice';
import routes from '../routes';

const useRoutes = () => {
    const userInfo = useSelector((state: ReduxState) => state.user);
    const dispatch = useDispatch();

    const userRole = useMemo(() => {
        return userInfo.type;
    }, [userInfo]);

    const isLoggedIn = useMemo(() => {
        return userInfo.id > 0;
    }, [userInfo]);

    const partialRoutes = useMemo(() => {
        if (isLoggedIn && !userInfo.privacy && userRole !== 'admin') {
            // if the user has not yet accepted the privacy policy and is not an admin, his only route is the consents page

            return [{
                name: 'Consents page',
                path: '/consents',
                showSidebar: false,
                component: <ConsentsExternalPage />,
                canAuth: true,
                requireAuth: true
            }];
        }

        if (isLoggedIn && !userInfo.companyId && userInfo.type === 'customer') {
            if (userInfo.enabledCompanies.length > 0) {
                const companyIdLocalStorage = parseInt(localStorage.getItem('company_id') ?? '', 10);

                if (companyIdLocalStorage && userInfo.enabledCompanies.find(c => c.id === companyIdLocalStorage)) {
                    dispatch(setCompanyId(companyIdLocalStorage));
                } else {
                    dispatch(setCompanyId(userInfo.enabledCompanies[0].id));
                    localStorage.setItem('company_id', userInfo.enabledCompanies[0].id.toString());
                }
            }

            return [{
                name: 'No company page',
                path: '/nocompany',
                showSidebar: false,
                component: <NoCompanyExternalPage />,
                canAuth: true,
                requireAuth: true
            }];
        }

        // otherwise return the routes defined in routes.tsx
        return routes.filter(route => (!route.roles || route.roles.includes(userRole)) && (isLoggedIn || !route.requireAuth));
    }, [dispatch, isLoggedIn, userInfo, userRole]);

    const finalRoutes = useMemo(() => {
        const finalRoutesImpl = [...partialRoutes];

        // co2 external tool
        finalRoutesImpl.push({
            name: 'CO2 tool',
            path: '/co2-simulator',
            showSidebar: false,
            component: <Co2SimulatorPage />,
            canAuth: false,
            exactPath: true
        });

        // fallback route: redirects the user to the home page if he tries to load a page that doesn't exist
        finalRoutesImpl.push({
            name: 'Fallback redirect',
            path: '*',
            showSidebar: false,
            component:  <Redirect to={partialRoutes[0]?.path ?? '/'} />,
            canAuth: true
        });

        return finalRoutesImpl;
    }, [partialRoutes]);

    return finalRoutes;
};

export default useRoutes;
